import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ShieldOff, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Pagination from '../../components/Pagination';
import { API } from './adminShared';

const fmt = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function SecurityTab({ authCfg }) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/geo-blocked`, { ...authCfg, params: { page: p, limit: 20 } });
      setLogs(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [authCfg, page]);

  useEffect(() => { load(page); }, [page, load]);

  return (
    <Card data-testid="security-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ShieldOff className="w-5 h-5 text-red-600" />
          Blocked Access Attempts
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => load(page)} disabled={loading} data-testid="security-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4">
          Access attempts denied by the country restriction policy. Attempts from the same IP are grouped per hour. {total > 0 && `Total records: ${total.toLocaleString()}`}
        </p>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3 text-sm font-semibold">IP Address</th>
              <th className="text-left p-3 text-sm font-semibold">Country</th>
              <th className="text-center p-3 text-sm font-semibold">Attempts</th>
              <th className="text-left p-3 text-sm font-semibold">First Attempt</th>
              <th className="text-left p-3 text-sm font-semibold">Last Attempt</th>
              <th className="text-left p-3 text-sm font-semibold">Last Path</th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map((log, idx) => (
                <tr key={`${log.ip}-${log.bucket}-${idx}`} className="border-b hover:bg-gray-50" data-testid={`blocked-row-${idx}`}>
                  <td className="p-3 text-sm font-mono font-medium">{log.ip}</td>
                  <td className="p-3 text-sm">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium">
                      {log.country_name || log.country_code}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-center font-semibold">{log.attempts}</td>
                  <td className="p-3 text-sm text-gray-600">{fmt(log.first_seen)}</td>
                  <td className="p-3 text-sm text-gray-600">{fmt(log.last_seen)}</td>
                  <td className="p-3 text-sm text-gray-500 font-mono text-xs">{log.last_path}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  {loading ? 'Loading...' : 'No blocked access attempts recorded'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
